import type { FormikValues } from 'formik';
import { useIntl } from 'react-intl';
import type { Schema } from 'yup';
import * as Yup from 'yup';

import { DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER } from '@models/questionnaire';

import { useGetQuestionnaire } from 'generated/questionnaire.generated';
import type {
  QuestionnaireItemModel,
  QuestionnaireResponseItemModel,
  GetQuestionnaireModelInput,
  QuestionnaireModel,
} from 'generated/types';

/** Parse the value from a QuestionnaireResponse item */
const getItemValue = (
  question: QuestionnaireItemModel,
  response: QuestionnaireResponseItemModel | undefined
) => {
  // Assumptions: 1 answer per question, no nested items
  const answer = response?.answer?.at(0);
  // TODO Fill this out
  switch (question.type) {
    case 'string':
      return answer?.valueString ?? '';
    case 'text':
      return answer?.valueString ?? '';
    case 'choice':
      return answer?.valueCoding?.code ?? '';
    case 'boolean':
      return answer?.valueBoolean ?? false;
    case 'group':
    case 'display':
    case 'question':
    case 'decimal':
    case 'integer':
    case 'date':
    case 'dateTime':
    case 'time':
    case 'url':
    case 'open-choice':
    case 'attachment':
    case 'reference':
    case 'quantity':
    default:
      throw new Error(`Questionnaire item type ${question.type} not supported`);
  }
};

/** Map Formik values into QuestionnaireResponse items */
const mapValuesToItems = (
  questionnaire: QuestionnaireModel,
  values: FormikValues
): QuestionnaireResponseItemModel[] => {
  const items: QuestionnaireResponseItemModel[] = [];
  for (const linkId in values) {
    const question = questionnaire.item?.find((i) => i.linkId === linkId);
    if (!question) {
      continue;
    }
    const item: QuestionnaireResponseItemModel = { linkId };
    const answer = values[linkId];
    switch (question.type) {
      // TODO Fill this out
      case 'string':
        item.answer = [{ valueString: answer }];
        break;
      case 'text':
        item.answer = [{ valueString: answer }];
        break;
      case 'choice':
        item.answer = [{ valueCoding: { code: answer } }];
        break;
      case 'boolean':
        item.answer = [{ valueBoolean: answer }];
        break;
      case 'group':
      case 'display':
      case 'question':
      case 'decimal':
      case 'integer':
      case 'date':
      case 'dateTime':
      case 'time':
      case 'url':
      case 'open-choice':
      case 'attachment':
      case 'reference':
      case 'quantity':
      default:
        throw new Error(
          `Questionnaire item type ${question.type} not supported`
        );
    }
    items.push(item);
  }
  return items;
};

/**
 * Fetch a questionnaire and process its schema into Formik helpers
 * @description Contains typical API response object as well as initialValues, validationSchema, and a transformer function to map formik values back to FHIR resources
 */
const useQuestionnaire = (
  props?: Pick<GetQuestionnaireModelInput, 'patient'> & { identifier?: string },
  skip?: boolean
) => {
  const { formatMessage } = useIntl();
  const response = useGetQuestionnaire({
    variables: {
      input: {
        patient: props?.patient,
        identifier: props?.identifier ?? DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER,
      },
    },
    skip,
  });

  const { data } = response;
  const questionnaire = response.data?.getQuestionnaire.questionnaire;

  const getItemSchema = (question: QuestionnaireItemModel) => {
    let schema: Schema = Yup.string();
    // TODO Fill this out
    switch (question.type) {
      case 'string':
        schema = Yup.string();
        break;
      case 'text':
        schema = Yup.string();
        break;
      case 'choice':
        schema = Yup.string().oneOf(
          (question.answerOption ?? []).map(
            ({ valueCoding }) => valueCoding?.code ?? ''
          )
        );
        break;
      case 'boolean':
        schema = Yup.boolean();
        break;
      case 'group':
      case 'display':
      case 'question':
      case 'decimal':
      case 'integer':
      case 'date':
      case 'dateTime':
      case 'time':
      case 'url':
      case 'open-choice':
      case 'attachment':
      case 'reference':
      case 'quantity':
      default:
        throw new Error(
          `Questionnaire item type ${question.type} not supported`
        );
    }
    if (question.required) {
      schema = schema.required(formatMessage({ id: 'GLOBAL.required' }));
    } else {
      schema = schema.nullable();
    }
    return schema;
  };

  // Generate validation schema
  const schemaFields = (questionnaire?.item ?? []).reduce<Yup.ObjectShape>(
    (acc, curr) => {
      acc[curr.linkId] = getItemSchema(curr);
      return acc;
    },
    {}
  );
  const validationSchema = Yup.object().shape(schemaFields);

  // Generate initial values
  const initialValues: FormikValues = (
    questionnaire?.item ?? []
  ).reduce<FormikValues>((acc, curr) => {
    // Find given answer
    const response = data?.getQuestionnaire.responses?.item?.find(
      ({ linkId }) => linkId === curr.linkId
    );
    acc[curr.linkId] = getItemValue(curr, response);
    return acc;
  }, {});

  const _mapValuesToItems = (values: FormikValues) => {
    if (!questionnaire) {
      return [];
    }
    return mapValuesToItems(questionnaire, values);
  };

  return {
    ...response,
    formHelpers: {
      schemaFields,
      validationSchema,
      initialValues,
      mapValuesToItems: _mapValuesToItems,
    },
  };
};

export default useQuestionnaire;
